import { useDeletePlacementTestMutation } from '@/app/redux/endpoints/assessments';
import { useAppDispatch } from '@/app/redux/hooks';
import { useAssessments } from '@/app/redux/hooks/assessments';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import { ControllerType } from '@/common/types/student';
import InitialsCircle from '@/common/ui/InitialsCircle';
import {
  DotsThreeVerticalIcon,
  MouseLeftClickIcon,
  TrashIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shadcn/components/dropdown-menu';
import { Label } from '@shadcn/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shadcn/components/select';
import { TableCell, TableRow } from '@shadcn/components/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import dayjs from 'dayjs';
import { Info, Keyboard } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface PlacementTestRowProps {
  placementTest: PlacementTest;
  onViewResult: (result: PlacementTestResult) => void;
}

export default function PlacementTestRow({
  placementTest,
  onViewResult,
}: PlacementTestRowProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { currentOrganisationId } = useCurrentOrganisationId();
  const { studentsById } = useStudents();
  const student = studentsById.get(placementTest.studentId);
  const { placementTestResults } = useAssessments();
  const [deletePlacementTest] = useDeletePlacementTestMutation();

  const result = placementTestResults.find(
    (r) => r.placementTestId === placementTest.id,
  );

  const [isDropdownOpen, setDropdownOpen] = useState(false);

  function handleConfirmDelete() {
    setConfirmationDialogueCallback(async () => {
      await deletePlacementTest({
        orgId: currentOrganisationId!,
        placementTestId: placementTest.id,
      }).unwrap();
    });
    dispatch(
      openConfirmationDialogue({
        title: 'Delete Placement Test',
        description: [
          'Are you sure you want to delete this placement test? This cannot be undone.',
          'If any learners have already completed this assessment, their results will also be deleted.',
        ],
        variant: 'destructive',
      }),
    );
  }

  const renderStudentAvatar = () => {
    if (!student) return null;
    return (
      <Tooltip>
        <TooltipTrigger
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/dashboard/learners/${student.id}`);
          }}
          className="p-2"
        >
          <InitialsCircle
            userName={`${student.studentFirstName} ${student.studentSurname}`}
            size={24}
          />
        </TooltipTrigger>
        <TooltipContent>
          {student.studentFirstName} {student.studentSurname}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TableRow
      className={cn(result && 'cursor-pointer')}
      onClick={() => result && onViewResult(result)}
    >
      <TableCell className="pl-4 text-sm w-[180px]">
        <div className="flex gap-2">
          {dayjs(placementTest.createdAt).format('DD/MM/YY h:mm a')}
          <Tooltip>
            <TooltipTrigger>
              <Info color="hsl(var(--primary))" size={16} />
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <strong>Legacy Placement Test</strong>
              <br />
              Placement Tests have been replaced with Assessments and can no
              longer be completed, but you can still view results for existing
              ones.
            </TooltipContent>
          </Tooltip>
        </div>
      </TableCell>
      <TableCell className="px-4">
        {!placementTest.completed && renderStudentAvatar()}
      </TableCell>
      {/* Empty cell for "started" column */}
      <TableCell />
      <TableCell className="px-4">
        {placementTest.completed && renderStudentAvatar()}
      </TableCell>
      <TableCell className="px-4">
        <Select value={placementTest.phase.toString()} disabled={true}>
          <SelectTrigger id="phase">
            <span className="font-medium">Phase {placementTest.phase}</span>
          </SelectTrigger>
        </Select>
      </TableCell>
      <TableCell className="px-4">
        <Select value={placementTest.control} disabled={true}>
          <SelectTrigger id="control">
            <SelectValue placeholder="Select control type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ControllerType.KEYBOARD}>
              <span className="flex items-center gap-2">
                <Keyboard size={16} />
                Typing
              </span>
            </SelectItem>
            <SelectItem value={ControllerType.CLICK}>
              <span className="flex items-center gap-2">
                <MouseLeftClickIcon size={16} />
                Click to Place
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="px-4">
        <Label
          className="flex items-center gap-2 text-sm text-muted-foreground w-fit"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={placementTest.placeAtAssessedLevel}
            disabled={true}
          />
          <span>Place automatically</span>
        </Label>
      </TableCell>
      <TableCell className="w-[100px]">
        <div className="ml-4">
          <DropdownMenu open={isDropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="p-1/2 mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                <DotsThreeVerticalIcon size={24} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onClick={(e) => e.stopPropagation()}
            >
              <DropdownMenuItem
                className="flex gap-2"
                onClick={() => {
                  setDropdownOpen(false);
                  handleConfirmDelete();
                }}
              >
                <TrashIcon weight="bold" size={16} color="red" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}
